/**
 * Money.
 *
 * Integer cents, always. Floats produce rounding errors that surface months
 * later as a total that is one cent off and impossible to explain, and it is
 * the most common bug in software that handles money.
 *
 * The branded type makes it awkward to pass a raw number where cents are
 * expected, which is the point.
 */

export type Cents = number & { readonly __brand: 'Cents' }

export function cents(value: number): Cents {
  if (!Number.isInteger(value)) {
    throw new Error(`Money must be whole cents, got ${value}`)
  }
  return value as Cents
}

/** Parse what a person or an OCR field gives us. Belgian format aware. */
export function parseAmountToCents(input: string): Cents | null {
  if (!input) return null

  let s = input
    .replace(/[€\s ]/g, '')
    .replace(/EUR/gi, '')
    .trim()

  if (!s) return null

  const negative = /^-/.test(s) || /^\(.*\)$/.test(s)
  s = s.replace(/[()\-]/g, '')

  const lastComma = s.lastIndexOf(',')
  const lastDot = s.lastIndexOf('.')

  if (lastComma > -1 && lastDot > -1) {
    // Both present: whichever comes last is the decimal separator.
    // "1.286,40" (BE/NL) vs "1,286.40" (UK/US)
    if (lastComma > lastDot) s = s.replace(/\./g, '').replace(',', '.')
    else s = s.replace(/,/g, '')
  } else if (lastComma > -1) {
    // One or two digits after the separator means it is a decimal point.
    // A thousands group is always exactly three digits, so "1,286" groups
    // but "1,5" and "1,50" are amounts. Accepting one digit matters because
    // Azure returns JSON floats, and 1289.70 arrives as 1289.7 — under a
    // strict two-digit rule that silently became €12 897,00.
    const after = s.length - lastComma - 1
    s = after >= 1 && after <= 2 ? s.replace(',', '.') : s.replace(/,/g, '')
  } else if (lastDot > -1) {
    const after = s.length - lastDot - 1
    if (after < 1 || after > 2) s = s.replace(/\./g, '')
  }

  if (!/^\d+(\.\d+)?$/.test(s)) return null

  const value = Math.round(Number(s) * 100)
  if (!Number.isFinite(value)) return null

  return cents(negative ? -value : value)
}

/** Display: "€1.286,40" in Belgian Dutch. */
export function formatCents(value: Cents | number, locale = 'nl-BE'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
  }).format(value / 100)
}

/** For the EPC QR payload, which requires a dot and exactly two decimals. */
export function centsToPayloadAmount(value: Cents | number): string {
  return (value / 100).toFixed(2)
}
